import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, STATUS_META, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import Logo from "../components/Logo";
import { videoEmbedUrl } from "../components/ProposalSections";
import {
  ArrowLeft, Plus, Trash2, Type, LayoutPanelTop, Table2, Sparkles,
  Save, Send, Copy, BarChart3, GripVertical, X, Check, CheckCheck,
  Target, ListOrdered, BarChart2, Users, Quote, HelpCircle, Image as ImageIcon, Video, Loader2, Wand2, Upload, Palette, GitBranch,
  MessageSquare,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

const SECTION_TYPES = [
  { type: "cover", label: "Couverture", icon: LayoutPanelTop },
  { type: "text", label: "Texte", icon: Type },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "video", label: "Vidéo", icon: Video },
  { type: "goals", label: "Objectifs", icon: Target },
  { type: "process", label: "Processus", icon: ListOrdered },
  { type: "stats", label: "Chiffres", icon: BarChart2 },
  { type: "team", label: "Équipe", icon: Users },
  { type: "pricing", label: "Tarification", icon: Table2 },
  { type: "testimonials", label: "Témoignages", icon: Quote },
  { type: "faq", label: "FAQ", icon: HelpCircle },
  { type: "diagram", label: "Schéma", icon: GitBranch },
];

// Field config for list-based section types
const ITEM_FIELDS = {
  goals: [{ key: "title", ph: "Titre de l'objectif" }, { key: "content", ph: "Description", area: true }],
  process: [{ key: "title", ph: "Nom de l'étape" }, { key: "content", ph: "Description", area: true }],
  stats: [{ key: "name", ph: "Valeur (ex : 99,9 %)" }, { key: "description", ph: "Libellé", area: true }],
  team: [{ key: "image", ph: "URL de la photo" }, { key: "name", ph: "Nom" }, { key: "description", ph: "Rôle / bio", area: true }],
  testimonials: [{ key: "content", ph: "Citation", area: true }, { key: "name", ph: "Auteur" }, { key: "description", ph: "Entreprise" }, { key: "image", ph: "URL de la photo (optionnel)" }],
  faq: [{ key: "title", ph: "Question" }, { key: "content", ph: "Réponse", area: true }],
};

const newItem = (type) => {
  if (type === "pricing") return { id: uid(), name: "", description: "", quantity: 1, unit_price: 0, recurrence: "mensuel" };
  return { id: uid(), title: "", content: "", name: "", description: "", image: "" };
};

const SECTION_LABEL = Object.fromEntries(SECTION_TYPES.map((t) => [t.type, t.label]));

const OFFER_TYPES = {
  oxiwi: ["Fibre Pro Entreprise", "Flotte Mobile", "Téléphonie VoIP", "Réseau Multi-sites"],
  cleya: ["Branding & Identité", "Génération de leads", "Landing Page", "Développement web", "SEO", "Publicité digitale"],
};

export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeCompany, brand } = useAuth();
  const [p, setP] = useState(null);
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [prospectUrl, setProspectUrl] = useState("");
  const [extractingProspect, setExtractingProspect] = useState(false);
  const [brandMode, setBrandMode] = useState("web"); // "web" | "manual"
  const [manualBrand, setManualBrand] = useState({ name: "", accent_hex: "#0047FF", logo_file_id: null });
  const [uploadingProspectLogo, setUploadingProspectLogo] = useState(false);
  const [savingManualBrand, setSavingManualBrand] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState(null); // null = pas encore chargé
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatFetching, setChatFetching] = useState(false);
  const prospectLogoRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    api.get(`/proposals/${id}`).then((r) => {
      setP(r.data);
      setActive(r.data.sections[0]?.id || null);
    }).catch(() => { toast.error("Proposition introuvable"); navigate("/proposals"); });
    api.get("/clients").then((r) => setClients(r.data));
  }, [id, navigate]);

  const computeTotal = (sections) => {
    let t = 0;
    sections.forEach((s) => s.type === "pricing" && (s.items || []).forEach((it) => { t += (Number(it.quantity) || 0) * (Number(it.unit_price) || 0); }));
    return t;
  };

  const mutate = (updater) => {
    setP((prev) => { const next = updater({ ...prev }); dirty.current = true; return next; });
  };

  const save = useCallback(async (silent = false) => {
    if (!p) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/proposals/${id}`, {
        title: p.title, clientId: p.client_id, clientName: p.client_name,
        sections: p.sections, currency: p.currency, expiresAt: p.expires_at || null,
        depositType: p.deposit_value != null
          ? (p.deposit_type || brand?.deposit_type || null)
          : (p.deposit_type || null),
        depositValue: p.deposit_value != null ? Number(p.deposit_value) : null,
      });
      dirty.current = false;
      if (!silent) toast.success("Enregistré");
      setP((prev) => ({ ...prev, total: data.total }));
    } catch (e) { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  }, [p, id]);

  // autosave
  useEffect(() => {
    const t = setInterval(() => { if (dirty.current) save(true); }, 4000);
    return () => clearInterval(t);
  }, [save]);

  // Autoscroll vers le dernier message à chaque ajout / pendant la génération.
  // Placé AVANT tout early return pour garder un ordre de hooks stable.
  useEffect(() => {
    if (chatOpen) chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatHistory, chatLoading, chatFetching, chatOpen]);

  if (!p) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;

  const LIST_TYPES = ["goals", "process", "stats", "team", "pricing", "testimonials", "faq"];
  const addSection = (type) => {
    const defaultTitle = type === "cover" ? "Couverture" : (SECTION_LABEL[type] && type !== "text") ? SECTION_LABEL[type] : "Nouvelle section";
    const s = {
      id: uid(), type, title: defaultTitle, content: "",
      items: LIST_TYPES.includes(type) ? [newItem(type)] : [],
    };
    mutate((d) => ({ ...d, sections: [...d.sections, s] }));
    setActive(s.id);
  };

  const updateSection = (sid, patch) => mutate((d) => ({ ...d, sections: d.sections.map((s) => s.id === sid ? { ...s, ...patch } : s) }));
  const removeSection = (sid) => mutate((d) => ({ ...d, sections: d.sections.filter((s) => s.id !== sid) }));

  const updateItem = (sid, iid, patch) => updateSection(sid, {
    items: p.sections.find((s) => s.id === sid).items.map((it) => it.id === iid ? { ...it, ...patch } : it),
  });
  const addItem = (sid) => {
    const sec = p.sections.find((s) => s.id === sid);
    updateSection(sid, { items: [...sec.items, newItem(sec.type)] });
  };
  const removeItem = (sid, iid) => updateSection(sid, { items: p.sections.find((s) => s.id === sid).items.filter((it) => it.id !== iid) });

  const setStatus = async (status) => {
    await save(true);
    const { data } = await api.patch(`/proposals/${id}/status`, { status });
    setP((prev) => ({ ...prev, status: data.status }));
    toast.success(status === "envoyée" ? "Proposition marquée comme envoyée" : "Statut mis à jour");
  };

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/p/${p.public_id}`); toast.success("Lien public copié"); };

  const extractProspect = async () => {
    if (!prospectUrl.trim()) return;
    setExtractingProspect(true);
    try {
      const url = prospectUrl.trim().startsWith("http") ? prospectUrl.trim() : `https://${prospectUrl.trim()}`;
      const { data } = await api.post(`/proposals/${id}/extract-prospect-brand`, { url });
      if (data.ok) {
        setP((prev) => ({ ...prev, prospect_brand: data.prospect_brand }));
        toast.success("Charte prospect extraite");
        setProspectUrl("");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Extraction échouée");
    } finally {
      setExtractingProspect(false);
    }
  };

  const clearProspectBrand = async () => {
    try {
      await api.put(`/proposals/${id}`, { prospectBrand: null });
      setP((prev) => ({ ...prev, prospect_brand: null }));
      setManualBrand({ name: "", accent_hex: "#0047FF", logo_file_id: null });
    } catch { /* silent */ }
  };

  // Décrit une action proposée par le conseiller (libellé lisible, sans l'appliquer).
  const describeAction = (a) => {
    if (a.type === 'add_section') return `Ajouter la section « ${a.title || a.section_type || 'nouvelle'} »`;
    const sec = (p?.sections || []).find((s) => s.id === a.id);
    const name = sec?.title || 'section';
    if (a.type === 'update_section') return `Modifier « ${name} »`;
    if (a.type === 'delete_section') return `Supprimer « ${name} »`;
    return 'Modification';
  };

  // Applique une liste d'actions du conseiller aux sections de la proposition.
  const applyActions = (actions) => {
    if (!actions?.length) return;
    mutate((d) => {
      let secs = [...d.sections];
      for (const a of actions) {
        if (a.type === 'update_section') {
          secs = secs.map((s) => s.id === a.id ? { ...s, ...(a.title !== undefined && { title: a.title }), ...(a.content !== undefined && { content: a.content }), ...(a.items !== undefined && { items: a.items.map((it) => ({ ...it, id: it.id || uid() })) }) } : s);
        } else if (a.type === 'add_section') {
          const newSec = { id: uid(), type: a.section_type || 'text', title: a.title || '', content: a.content || '', items: (a.items || []).map((it) => ({ ...it, id: it.id || uid() })) };
          const idx = a.after_id ? secs.findIndex((s) => s.id === a.after_id) : -1;
          if (idx >= 0) secs.splice(idx + 1, 0, newSec);
          else secs.push(newSec);
        } else if (a.type === 'delete_section') {
          secs = secs.filter((s) => s.id !== a.id);
        }
      }
      return { ...d, sections: secs };
    });
    setTimeout(() => save(true), 300);
  };

  // Accepte/refuse une modification proposée (par index de message + d'action).
  const decideAction = (msgIdx, actIdx, accept) => {
    const item = chatHistory?.[msgIdx]?.pendingActions?.[actIdx];
    if (!item || item.status !== 'pending') return;
    if (accept) applyActions([item.action]);
    setChatHistory((hist) => hist.map((m, i) => i !== msgIdx ? m : {
      ...m, pendingActions: m.pendingActions.map((x, j) => j !== actIdx ? x : { ...x, status: accept ? 'accepted' : 'rejected' }),
    }));
  };

  // Tout accepter / tout refuser les modifications encore en attente d'un message.
  const decideAll = (msgIdx, accept) => {
    const msg = chatHistory?.[msgIdx];
    if (!msg?.pendingActions) return;
    if (accept) applyActions(msg.pendingActions.filter((x) => x.status === 'pending').map((x) => x.action));
    setChatHistory((hist) => hist.map((m, i) => i !== msgIdx ? m : {
      ...m, pendingActions: m.pendingActions.map((x) => x.status === 'pending' ? { ...x, status: accept ? 'accepted' : 'rejected' } : x),
    }));
  };

  const sendChatMessage = async (preset) => {
    const text = (typeof preset === 'string' ? preset : chatInput).trim();
    if (!text || chatLoading) return;
    const currentHistory = chatHistory || [];
    const userMsg = { role: 'user', content: text };
    const nextHistory = [...currentHistory, userMsg];
    setChatHistory(nextHistory);
    setChatInput('');
    setChatLoading(true);
    try {
      const { data } = await api.post(`/proposals/${p.id}/chat`, {
        message: userMsg.content,
        history: currentHistory.slice(-6),
      });
      // Ne plus appliquer automatiquement : chaque modification est proposée
      // avec des boutons Accepter/Refuser (+ Tout accepter).
      const pendingActions = (data.actions || []).map((a) => ({ action: a, label: describeAction(a), status: 'pending' }));
      const assistantMsg = { role: 'assistant', content: data.reply, pendingActions };
      setChatHistory([...nextHistory, assistantMsg]);
    } catch {
      toast.error('Erreur du conseiller IA');
    } finally {
      setChatLoading(false);
    }
  };

  const uploadProspectLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProspectLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setManualBrand((m) => ({ ...m, logo_file_id: data.id }));
    } catch { toast.error("Échec de l'upload du logo"); }
    finally { setUploadingProspectLogo(false); }
  };

  const saveManualBrand = async () => {
    if (!manualBrand.name.trim() && !manualBrand.logo_file_id) return;
    setSavingManualBrand(true);
    try {
      const brand = {
        name: manualBrand.name.trim() || null,
        accent_hex: manualBrand.accent_hex || null,
        logo_file_id: manualBrand.logo_file_id || null,
        url: null, tagline: null, industry: null, context: null,
      };
      await api.put(`/proposals/${id}`, { prospectBrand: brand });
      setP((prev) => ({ ...prev, prospect_brand: brand }));
      toast.success("Charte prospect enregistrée");
    } catch { toast.error("Enregistrement échoué"); }
    finally { setSavingManualBrand(false); }
  };

  const total = computeTotal(p.sections);
  const meta = STATUS_META[p.status] || STATUS_META.brouillon;

  return (
    <div className="flex flex-col overflow-hidden bg-muted" style={{ height: '100dvh' }}>
      {/* Top bar — safe-area-inset-top pour PWA iOS (viewport-fit=cover) */}
      <header className="bg-background border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0"
        style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(64px + env(safe-area-inset-top))' }}>
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/proposals")} data-testid="editor-back" className="p-2 rounded-sm hover:bg-muted"><ArrowLeft className="w-5 h-5" /></button>
          <Logo brand={brand} />
          <span className={`hidden sm:inline text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/proposals/${id}/analytics`)} className="rounded-sm hidden sm:flex" data-testid="editor-analytics">
            <BarChart3 className="w-4 h-4 mr-1" /> Suivi
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
  setChatOpen(true);
  if (chatHistory === null) {
    setChatFetching(true);
    api.get(`/proposals/${p.id}/chat`).then(r => setChatHistory(r.data)).catch(() => setChatHistory([])).finally(() => setChatFetching(false));
  }
}}
            className="rounded-sm hidden md:flex items-center gap-1.5" data-testid="editor-chat">
            <MessageSquare className="w-4 h-4" />
            <span>Conseiller</span>
          </Button>
          <Button variant="outline" size="icon" onClick={() => {
  setChatOpen(true);
  if (chatHistory === null) {
    setChatFetching(true);
    api.get(`/proposals/${p.id}/chat`).then(r => setChatHistory(r.data)).catch(() => setChatHistory([])).finally(() => setChatFetching(false));
  }
}}
            className="rounded-sm md:hidden" title="Conseiller IA" data-testid="editor-chat-mobile">
            <MessageSquare className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => save(false)} disabled={saving} className="rounded-sm" data-testid="editor-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? "…" : "Enregistrer"}
          </Button>
          <Button size="sm" onClick={() => setStatus("envoyée")} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="editor-send">
            <Send className="w-4 h-4 mr-1" /> Envoyer
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar — sections */}
        <aside className="hidden md:flex flex-col w-60 border-r border-border bg-background shrink-0 overflow-y-auto">
          <div className="p-4">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Sections</p>
            <div className="space-y-1">
              {p.sections.map((s) => (
                <button key={s.id} onClick={() => setActive(s.id)} data-testid={`section-tab-${s.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left transition-all duration-150 ${active === s.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-foreground"}`}>
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="truncate flex-1">{s.title || "Sans titre"}</span>
                </button>
              ))}
              {p.sections.length === 0 && <p className="text-sm text-muted-foreground px-1">Ajoutez votre première section.</p>}
            </div>
          </div>
          <div className="p-4 border-t border-border mt-auto">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Ajouter</p>
            <div className="grid grid-cols-2 gap-1.5">
              {SECTION_TYPES.map((t) => (
                <button key={t.type} onClick={() => addSection(t.type)} data-testid={`add-${t.type}`}
                  className="flex items-center gap-1.5 px-2 py-2 rounded-sm text-xs border border-border hover:border-primary hover:text-primary transition-all duration-150">
                  <t.icon className="w-3.5 h-3.5 shrink-0" /> {t.label}
                </button>
              ))}
            </div>
            <button onClick={() => setAiOpen(true)} data-testid="ai-generate-open"
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm text-sm bg-foreground text-white hover:bg-primary transition-all duration-150 mt-2">
              <Sparkles className="w-4 h-4" /> Générer avec l'IA
            </button>
          </div>
        </aside>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="bg-white border border-border shadow-sm mx-auto max-w-4xl rounded-sm">
            <div className="p-8 lg:p-12 space-y-10">
              {p.sections.map((s) => (
                <SectionBlock key={s.id} s={s} currency={p.currency}
                  onChange={(patch) => updateSection(s.id, patch)}
                  onRemove={() => removeSection(s.id)}
                  onItem={(iid, patch) => updateItem(s.id, iid, patch)}
                  onAddItem={() => addItem(s.id)}
                  onRemoveItem={(iid) => removeItem(s.id, iid)}
                  focus={() => setActive(s.id)}
                  proposalId={p.id}
                  companySlug={activeCompany}
                />
              ))}
              {p.sections.length > 0 && (
                <div className="flex items-center justify-between pt-6 border-t-2 border-foreground">
                  <span className="font-heading text-lg font-semibold">Total</span>
                  <span className="font-heading text-2xl font-bold" data-testid="canvas-total">{fmtMoney(total, p.currency)}</span>
                </div>
              )}
              {p.sections.length === 0 && (
                <div className="text-center py-20 text-muted-foreground">
                  <p className="mb-4">Document vide.</p>
                  <Button onClick={() => addSection("cover")} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="empty-add-section"><Plus className="w-4 h-4 mr-1" /> Ajouter une section</Button>
                </div>
              )}
            </div>
          </div>
          {/* Mobile add — safe-area-inset-bottom pour le home indicator iOS */}
          <div className="md:hidden flex flex-wrap gap-2 justify-center mt-6" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {SECTION_TYPES.map((t) => (
              <Button key={t.type} variant="outline" size="sm" onClick={() => addSection(t.type)} className="rounded-sm"><t.icon className="w-4 h-4 mr-1" />{t.label}</Button>
            ))}
            <Button size="sm" onClick={() => setAiOpen(true)} className="rounded-sm bg-foreground"><Sparkles className="w-4 h-4 mr-1" /> IA</Button>
          </div>
        </div>

        {/* Right properties */}
        <aside className="hidden lg:flex flex-col w-80 border-l border-border bg-background shrink-0 overflow-y-auto p-6 space-y-6">

          {/* ── 1. PROSPECT ── */}
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Prospect</p>

            {/* Client select */}
            <label className="text-sm font-medium">Client</label>
            <Select value={p.client_id || "none"} onValueChange={(v) => {
              const c = clients.find((x) => x.id === v);
              mutate((d) => ({ ...d, client_id: v === "none" ? null : v, client_name: c ? (c.company || c.name) : "" }));
            }}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="prop-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun client</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Charte prospect — co-localisée avec le client */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Charte prospect</label>
                {p.prospect_brand && (
                  <button onClick={clearProspectBrand} className="text-[11px] text-muted-foreground hover:text-destructive transition-colors cursor-pointer">Supprimer</button>
                )}
              </div>

              {p.prospect_brand ? (
                /* ── Preview brand (extrait ou manuel) ── */
                <div className="rounded-sm border border-border bg-muted/40 p-3 space-y-2">
                  <div className="flex items-center gap-2.5">
                    {p.prospect_brand.logo_file_id ? (
                      <img src={fileUrl(p.prospect_brand.logo_file_id)} alt={p.prospect_brand.name} className="h-8 w-auto max-w-[80px] object-contain shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-sm bg-foreground/10 flex items-center justify-center text-sm font-bold shrink-0">
                        {(p.prospect_brand.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate leading-tight">{p.prospect_brand.name || "Extrait"}</p>
                      {p.prospect_brand.industry && <p className="text-[11px] text-muted-foreground truncate">{p.prospect_brand.industry}</p>}
                    </div>
                    {p.prospect_brand.accent_hex && (
                      <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm shrink-0" style={{ backgroundColor: p.prospect_brand.accent_hex }} title={p.prospect_brand.accent_hex} />
                    )}
                  </div>
                  {p.prospect_brand.context && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-3">{p.prospect_brand.context}</p>
                  )}
                  {p.prospect_brand.context && (
                    <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                      <Sparkles className="w-3 h-3" /> Contexte IA disponible
                    </div>
                  )}
                  {/* Re-scraper uniquement si source web */}
                  {p.prospect_brand.url && (
                    <div className="flex gap-1.5 pt-1">
                      <Input placeholder="Nouveau site…" value={prospectUrl} onChange={(e) => setProspectUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && extractProspect()} className="rounded-sm text-xs h-7" />
                      <Button size="sm" onClick={extractProspect} disabled={extractingProspect || !prospectUrl.trim()} className="rounded-sm shrink-0 h-7 px-2" title="Re-scraper">
                        {extractingProspect ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Formulaire initial : toggle Web / Manuel ── */
                <div>
                  {/* Toggle mode */}
                  <div className="flex rounded-sm border border-border overflow-hidden mb-3">
                    {[["web", Wand2, "Site web"], ["manual", Palette, "Manuel"]].map(([mode, Icon, label]) => (
                      <button key={mode} type="button" onClick={() => setBrandMode(mode)}
                        className={`flex-1 h-8 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors cursor-pointer ${brandMode === mode ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-muted"}`}>
                        <Icon className="w-3 h-3" />{label}
                      </button>
                    ))}
                  </div>

                  {brandMode === "web" ? (
                    /* Mode scraping */
                    <div>
                      <div className="flex gap-1.5">
                        <Input placeholder="https://prospect.com" value={prospectUrl} onChange={(e) => setProspectUrl(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && extractProspect()} className="rounded-sm text-xs h-9" />
                        <Button size="sm" onClick={extractProspect} disabled={extractingProspect || !prospectUrl.trim()} className="rounded-sm shrink-0 h-9 px-2.5" title="Extraire la charte">
                          {extractingProspect ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1.5">Logo, couleurs et contexte IA extraits automatiquement.</p>
                    </div>
                  ) : (
                    /* Mode manuel */
                    <div className="space-y-2.5">
                      {/* Logo upload */}
                      <div>
                        <p className="text-[11px] text-muted-foreground mb-1.5">Logo</p>
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-sm border border-border bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                            {uploadingProspectLogo ? (
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            ) : manualBrand.logo_file_id ? (
                              <img src={fileUrl(manualBrand.logo_file_id)} alt="logo" className="w-full h-full object-contain" />
                            ) : (
                              <ImageIcon className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          <Button variant="outline" size="sm" className="rounded-sm text-xs h-8 cursor-pointer"
                            onClick={() => prospectLogoRef.current?.click()} disabled={uploadingProspectLogo}>
                            <Upload className="w-3 h-3 mr-1.5" /> Importer
                          </Button>
                          {manualBrand.logo_file_id && (
                            <button onClick={() => setManualBrand((m) => ({ ...m, logo_file_id: null }))}
                              className="text-muted-foreground hover:text-destructive transition-colors cursor-pointer">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <input ref={prospectLogoRef} type="file" accept="image/*" onChange={uploadProspectLogo} className="hidden" />
                        </div>
                      </div>

                      {/* Nom */}
                      <div>
                        <p className="text-[11px] text-muted-foreground mb-1">Nom du prospect</p>
                        <Input placeholder="ACME Corp" value={manualBrand.name}
                          onChange={(e) => setManualBrand((m) => ({ ...m, name: e.target.value }))}
                          className="rounded-sm text-xs h-8" />
                      </div>

                      {/* Couleur */}
                      <div>
                        <p className="text-[11px] text-muted-foreground mb-1">Couleur d'accent</p>
                        <div className="flex items-center gap-2">
                          <input type="color" value={manualBrand.accent_hex}
                            onChange={(e) => setManualBrand((m) => ({ ...m, accent_hex: e.target.value }))}
                            className="w-9 h-8 rounded-sm border border-border cursor-pointer p-0.5 bg-background" />
                          <Input value={manualBrand.accent_hex}
                            onChange={(e) => setManualBrand((m) => ({ ...m, accent_hex: e.target.value }))}
                            className="rounded-sm text-xs h-8 font-mono flex-1" maxLength={7} />
                        </div>
                      </div>

                      <Button size="sm" className="w-full rounded-sm h-8 text-xs cursor-pointer"
                        onClick={saveManualBrand}
                        disabled={savingManualBrand || (!manualBrand.name.trim() && !manualBrand.logo_file_id)}>
                        {savingManualBrand ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                        Appliquer la charte
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── 2. DOCUMENT ── */}
          <div className="border-t border-border pt-6 space-y-4">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Document</p>
            <div>
              <label className="text-sm font-medium">Titre</label>
              <Input value={p.title} onChange={(e) => mutate((d) => ({ ...d, title: e.target.value }))} data-testid="prop-title" className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Devise</label>
              <Select value={p.currency} onValueChange={(v) => mutate((d) => ({ ...d, currency: v }))}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="prop-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="CHF">CHF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Expiration</label>
              <input type="date" value={p.expires_at ? new Date(p.expires_at).toISOString().split("T")[0] : ""}
                onChange={(e) => mutate((d) => ({ ...d, expires_at: e.target.value || null }))}
                data-testid="prop-expires-at"
                className="mt-1.5 w-full h-9 rounded-sm border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {/* ── 3. ACOMPTE (conditionnel) ── */}
          {brand?.require_deposit && (
            <div className="border-t border-border pt-6 space-y-3">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Acompte</p>
              <div className="flex rounded-sm border border-border overflow-hidden">
                {[["percentage", "%"], ["fixed", brand.currency || "€"]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => mutate((d) => ({ ...d, deposit_type: d.deposit_type === v ? null : v }))}
                    className={`flex-1 h-8 text-xs font-medium transition-colors cursor-pointer ${(p.deposit_type || brand.deposit_type) === v ? "bg-primary text-white" : "bg-transparent text-muted-foreground hover:bg-muted"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <div className="relative">
                <input type="number" min={0} value={p.deposit_value != null ? p.deposit_value : ""}
                  onChange={(e) => mutate((d) => ({ ...d, deposit_value: e.target.value === "" ? null : e.target.value }))}
                  placeholder={brand.deposit_value != null ? String(brand.deposit_value) : ""}
                  data-testid="prop-deposit-value"
                  className="w-full h-9 rounded-sm border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {(p.deposit_type || brand.deposit_type) === "fixed" ? (brand.currency || "€") : "%"}
                </span>
              </div>
              {(p.deposit_value == null && p.deposit_type == null) && (
                <p className="text-xs text-muted-foreground">Hérite de la société ({brand.deposit_type === "percentage" ? `${brand.deposit_value} %` : brand.deposit_value})</p>
              )}
            </div>
          )}

          {/* ── 4. PUBLICATION ── */}
          <div className="border-t border-border pt-6 space-y-3">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Publication</p>
            <Select value={p.status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-sm" data-testid="prop-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={copyLink} className="w-full rounded-sm justify-start cursor-pointer" data-testid="prop-copy-link">
              <Copy className="w-4 h-4 mr-2" /> Copier le lien client
            </Button>
            <Button variant="ghost" onClick={() => window.open(`/p/${p.public_id}`, "_blank")} className="w-full rounded-sm justify-start text-muted-foreground cursor-pointer" data-testid="prop-preview">
              Aperçu côté client
            </Button>
          </div>

          {/* ── 5. TOTAL ── */}
          <div className="border-t border-border pt-6 mt-auto">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Montant total</span>
              <span className="font-heading text-xl font-bold">{fmtMoney(total, p.currency)}</span>
            </div>
          </div>
        </aside>
      </div>

      <AIDialog open={aiOpen} setOpen={setAiOpen} clientName={p.client_name}
        prospectContext={p.prospect_brand?.context || null}
        offerTypes={OFFER_TYPES[activeCompany] || ["Offre standard", "Offre premium", "Sur mesure"]}
        onResult={(res) => {
          const newSections = res.sections.map((s) => ({ ...s, items: s.items || [] }));
          mutate((d) => ({ ...d, title: d.title === "Nouvelle proposition" ? res.title : d.title, sections: [...d.sections, ...newSections] }));
          if (newSections[0]) setActive(newSections[0].id);
        }} />

      {/* Chat contextuel IA */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={() => setChatOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-background border-l border-border flex flex-col shadow-xl animate-in slide-in-from-right duration-200" role="dialog" aria-label="Conseiller IA">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-sm bg-primary/10 text-primary shrink-0">
                  <Sparkles className="w-4 h-4" />
                </span>
                <div className="leading-tight">
                  <span className="block font-semibold text-sm">Conseiller IA</span>
                  <span className="block text-[11px] text-muted-foreground">Analyse & réécriture pour signer</span>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} aria-label="Fermer le conseiller"
                className="text-muted-foreground hover:text-foreground rounded-sm p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4" aria-live="polite" aria-busy={chatLoading || chatFetching}>
              {chatFetching && (
                <div className="flex justify-center mt-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!chatFetching && (chatHistory || []).length === 0 && (
                <div className="mt-6 space-y-4">
                  <div className="text-center space-y-1">
                    <span className="flex items-center justify-center w-11 h-11 mx-auto rounded-sm bg-primary/10 text-primary">
                      <Sparkles className="w-5 h-5" />
                    </span>
                    <p className="font-semibold text-sm">Votre conseiller commercial</p>
                    <p className="text-xs text-muted-foreground">Il analyse la proposition et réécrit ce qui bloque la signature.</p>
                  </div>
                  <button
                    onClick={() => sendChatMessage("Analyse toute la proposition et propose-moi les améliorations qui vont pousser la signature.")}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-semibold rounded-sm px-3 py-2.5 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 transition-colors cursor-pointer"
                    data-testid="chat-analyze-all">
                    <Sparkles className="w-4 h-4" /> Analyser toute la proposition
                  </button>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-muted-foreground px-0.5">Ou demandez précisément :</p>
                    {[
                      "Améliore l'accroche de couverture",
                      "Ajoute une section FAQ avec 3 objections",
                      "Rends la tarification plus lisible",
                      "Repère les passages peu convaincants",
                    ].map((sugg) => (
                      <button key={sugg} onClick={() => sendChatMessage(sugg)}
                        className="w-full text-left text-xs text-foreground/80 bg-muted/50 hover:bg-muted rounded-sm px-3 py-2 border border-transparent hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors cursor-pointer">
                        {sugg}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(chatHistory || []).map((msg, i) => (
                <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`rounded-sm px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'max-w-[85%] bg-primary text-primary-foreground'
                      : 'max-w-[95%] bg-muted text-foreground'
                  }`}>
                    {msg.content}
                  </div>
                  {msg.pendingActions?.length > 0 && (() => {
                    const pendingCount = msg.pendingActions.filter((x) => x.status === 'pending').length;
                    return (
                      <div className="mt-1.5 max-w-[85%] w-full space-y-1.5" data-testid={`chat-actions-${i}`}>
                        {pendingCount > 1 && (
                          <div className="flex gap-1.5">
                            <button onClick={() => decideAll(i, true)} data-testid={`accept-all-${i}`}
                              className="flex items-center gap-1 text-[11px] font-semibold bg-primary text-white rounded-sm px-2 py-1 hover:bg-primary/90 transition-colors">
                              <CheckCheck className="w-3 h-3" /> Tout accepter ({pendingCount})
                            </button>
                            <button onClick={() => decideAll(i, false)}
                              className="text-[11px] font-medium text-muted-foreground rounded-sm px-2 py-1 border border-border hover:bg-muted transition-colors">
                              Tout refuser
                            </button>
                          </div>
                        )}
                        {msg.pendingActions.map((x, j) => {
                          const a = x.action;
                          const isDelete = a.type === 'delete_section';
                          const cur = (p?.sections || []).find((s) => s.id === a.id);
                          const newContent = a.content || (Array.isArray(a.items) && a.items.length ? `${a.items.length} élément(s) mis à jour` : '');
                          const titleChanged = !isDelete && a.title && a.title !== cur?.title;
                          return (
                            <div key={j} className="border border-border rounded-sm bg-surface overflow-hidden">
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <span className="text-xs flex-1 min-w-0 font-medium truncate">{x.label}</span>
                                {x.status === 'pending' ? (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => decideAction(i, j, true)} data-testid={`accept-${i}-${j}`}
                                      title="Accepter" className="w-6 h-6 flex items-center justify-center rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => decideAction(i, j, false)}
                                      title="Refuser" className="w-6 h-6 flex items-center justify-center rounded-sm bg-muted text-muted-foreground border border-border hover:text-destructive hover:border-destructive/40 transition-colors">
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <span className={`text-[10px] font-semibold shrink-0 ${x.status === 'accepted' ? 'text-emerald-600' : 'text-muted-foreground line-through'}`}>
                                    {x.status === 'accepted' ? '✓ Appliqué' : 'Refusé'}
                                  </span>
                                )}
                              </div>
                              {/* Aperçu du changement proposé — pour valider en connaissance de cause */}
                              {x.status === 'pending' && !isDelete && (titleChanged || newContent) && (
                                <div className="px-2 pb-2 pt-1.5 border-t border-border/60 space-y-1">
                                  {titleChanged && (
                                    <p className="text-[11px]"><span className="text-muted-foreground">Nouveau titre : </span><span className="font-medium">{a.title}</span></p>
                                  )}
                                  {newContent && (
                                    <p className="text-[11px] text-muted-foreground whitespace-pre-wrap" style={{ display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                      {newContent.length > 400 ? newContent.slice(0, 400) + '…' : newContent}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start" aria-label="Le conseiller rédige…">
                  <div className="bg-muted rounded-sm px-3 py-2.5 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="p-3 border-t border-border shrink-0">
              <p className="text-[10px] text-muted-foreground mb-2">Le conseiller propose des modifications — vous les acceptez ou les refusez.</p>
              <div className="flex gap-2 items-end">
                <textarea
                  ref={chatInputRef}
                  value={chatInput}
                  onChange={(e) => {
                    setChatInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); if (chatInputRef.current) chatInputRef.current.style.height = 'auto'; } }}
                  placeholder="Votre question…"
                  aria-label="Message au conseiller IA"
                  rows={1}
                  className="flex-1 text-sm border border-border rounded-sm px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus:border-primary bg-transparent resize-none max-h-32 leading-relaxed"
                  disabled={chatLoading}
                />
                <Button size="icon" onClick={() => sendChatMessage()} disabled={chatLoading || !chatInput.trim()} aria-label="Envoyer" className="rounded-sm shrink-0">
                  {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionBlock({ s, currency, onChange, onRemove, onItem, onAddItem, onRemoveItem, focus, proposalId, companySlug }) {
  const isPricing = s.type === "pricing";
  const fields = ITEM_FIELDS[s.type];
  const [uploadingImg, setUploadingImg] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ content: fileUrl(data.id) });
      toast.success("Image ajoutée");
    } catch (err) { toast.error("Échec de l'upload"); }
    finally { setUploadingImg(false); }
  };

  return (
    <div className="group relative" onClick={focus} data-testid={`section-block-${s.id}`}>
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} data-testid={`remove-section-${s.id}`}
        className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white border border-border rounded-sm text-muted-foreground hover:text-destructive z-10">
        <X className="w-3.5 h-3.5" />
      </button>

      {s.type === "cover" ? (
        <div className="text-center py-8 border-b border-border">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-3">Proposition commerciale</p>
          <input value={s.title} onChange={(e) => onChange({ title: e.target.value })} data-testid={`cover-title-${s.id}`}
            className="w-full text-center font-heading text-3xl font-bold tracking-tight bg-transparent outline-none focus:bg-muted/40 rounded-sm" placeholder="Titre de la proposition" />
          <textarea value={s.content} onChange={(e) => onChange({ content: e.target.value })}
            className="w-full text-center text-muted-foreground mt-3 bg-transparent outline-none resize-none focus:bg-muted/40 rounded-sm" rows={2} placeholder="Accroche / sous-titre" />
        </div>
      ) : s.type === "text" ? (
        <div>
          <SectionTitleInput s={s} onChange={onChange} testid={`text-title-${s.id}`} />
          <textarea value={s.content} onChange={(e) => onChange({ content: e.target.value })} data-testid={`text-content-${s.id}`}
            className="w-full text-foreground/80 leading-relaxed bg-transparent outline-none resize-none focus:bg-muted/40 rounded-sm" rows={4} placeholder="Rédigez votre contenu…" />
        </div>
      ) : isPricing ? (
        <div>
          <SectionTitleInput s={s} onChange={onChange} />
          <div className="space-y-2">
            {(s.items || []).map((it) => (
              <div key={it.id} className="flex flex-wrap items-center gap-2 p-2 rounded-sm border border-border" data-testid={`pricing-item-${it.id}`}>
                <input value={it.name} onChange={(e) => onItem(it.id, { name: e.target.value })} placeholder="Prestation"
                  className="flex-1 min-w-[140px] text-sm font-medium bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1 py-1" data-testid={`item-name-${it.id}`} />
                <input value={it.description} onChange={(e) => onItem(it.id, { description: e.target.value })} placeholder="Description"
                  className="flex-1 min-w-[120px] text-sm text-muted-foreground bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1 py-1" />
                <input type="number" value={it.quantity} onChange={(e) => onItem(it.id, { quantity: e.target.value })}
                  className="w-14 text-sm text-center bg-muted rounded-sm px-1 py-1 outline-none" title="Quantité" data-testid={`item-qty-${it.id}`} />
                <input type="number" value={it.unit_price} onChange={(e) => onItem(it.id, { unit_price: e.target.value })}
                  className="w-20 text-sm text-right bg-muted rounded-sm px-2 py-1 outline-none font-mono" title="Prix unitaire" data-testid={`item-price-${it.id}`} />
                <select value={it.recurrence} onChange={(e) => onItem(it.id, { recurrence: e.target.value })} className="text-xs bg-muted rounded-sm px-1 py-1 outline-none">
                  <option value="mensuel">/mois</option>
                  <option value="unique">unique</option>
                </select>
                <button onClick={() => onRemoveItem(it.id)} data-testid={`remove-item-${it.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={onAddItem} data-testid={`add-item-${s.id}`} className="mt-3 flex items-center gap-1 text-sm text-primary font-medium hover:underline"><Plus className="w-4 h-4" /> Ajouter une ligne</button>
          <div className="flex justify-end mt-3 text-sm">
            <span className="text-muted-foreground mr-2">Sous-total :</span>
            <span className="font-mono font-semibold">{fmtMoney((s.items || []).reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0), currency)}</span>
          </div>
        </div>
      ) : s.type === "image" ? (
        <div>
          <input value={s.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Légende (optionnel)"
            className="w-full text-sm text-muted-foreground bg-transparent outline-none focus:bg-muted/40 rounded-sm mb-3 px-1 py-0.5" data-testid={`image-caption-${s.id}`} />
          {s.content ? (
            <div className="relative group/img">
              <img src={s.content} alt={s.title || ""} className="w-full rounded-sm border border-border object-cover" />
              <button onClick={() => onChange({ content: "" })} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-sm text-muted-foreground hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label data-testid={`image-upload-${s.id}`} className="flex flex-col items-center justify-center gap-2 h-44 rounded-sm border-2 border-dashed border-border cursor-pointer hover:border-primary transition-colors">
              {uploadingImg ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">{uploadingImg ? "Envoi…" : "Cliquez pour importer une image"}</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImg} />
            </label>
          )}
        </div>
      ) : s.type === "video" ? (
        <div>
          <input value={s.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Légende (optionnel)"
            className="w-full text-sm text-muted-foreground bg-transparent outline-none focus:bg-muted/40 rounded-sm mb-3 px-1 py-0.5" data-testid={`video-caption-${s.id}`} />
          <Input value={s.content || ""} onChange={(e) => onChange({ content: e.target.value })} placeholder="URL de la vidéo (YouTube, Vimeo, Loom ou .mp4)"
            className="rounded-sm mb-3" data-testid={`video-url-${s.id}`} />
          {s.content ? (
            videoEmbedUrl(s.content) ? (
              <div className="relative w-full rounded-sm overflow-hidden border border-border" style={{ paddingTop: "56.25%" }}>
                <iframe title={s.title || "Vidéo"} src={videoEmbedUrl(s.content)} allowFullScreen className="absolute inset-0 w-full h-full" />
              </div>
            ) : (
              <video src={s.content} controls className="w-full rounded-sm border border-border" />
            )
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 h-44 rounded-sm border-2 border-dashed border-border text-muted-foreground">
              <Video className="w-6 h-6" />
              <span className="text-sm">Collez l'URL d'une vidéo ci-dessus</span>
            </div>
          )}
        </div>
      ) : s.type === "diagram" ? (
        <DiagramSectionEditor s={s} onChange={onChange} proposalId={proposalId} companySlug={companySlug} />
      ) : (
        <div>
          <SectionTitleInput s={s} onChange={onChange} />
          <div className="space-y-3">
            {(s.items || []).map((it, idx) => (
              <div key={it.id} className="flex gap-3 p-3 rounded-sm border border-border" data-testid={`list-item-${it.id}`}>
                {(s.type === "goals" || s.type === "process") && (
                  <span className="font-heading text-lg font-bold text-primary/40 tabular-nums shrink-0 w-7">{String(idx + 1).padStart(2, "0")}</span>
                )}
                <div className="flex-1 space-y-1.5">
                  {fields.map((f) => (
                    f.area ? (
                      <textarea key={f.key} value={it[f.key] || ""} onChange={(e) => onItem(it.id, { [f.key]: e.target.value })} placeholder={f.ph} rows={2}
                        className="w-full text-sm text-muted-foreground bg-transparent outline-none resize-none focus:bg-muted/40 rounded-sm px-1 py-0.5" data-testid={`item-${f.key}-${it.id}`} />
                    ) : (
                      <input key={f.key} value={it[f.key] || ""} onChange={(e) => onItem(it.id, { [f.key]: e.target.value })} placeholder={f.ph}
                        className={`w-full text-sm bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1 py-0.5 ${f.key === "name" || f.key === "title" ? "font-medium" : "text-muted-foreground"}`} data-testid={`item-${f.key}-${it.id}`} />
                    )
                  ))}
                </div>
                <button onClick={() => onRemoveItem(it.id)} data-testid={`remove-item-${it.id}`} className="text-muted-foreground hover:text-destructive p-1 self-start"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={onAddItem} data-testid={`add-item-${s.id}`} className="mt-3 flex items-center gap-1 text-sm text-primary font-medium hover:underline"><Plus className="w-4 h-4" /> Ajouter un élément</button>
        </div>
      )}
    </div>
  );
}

function SectionTitleInput({ s, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="w-2 h-2 bg-primary rotate-45 shrink-0" />
      <input value={s.title} onChange={(e) => onChange({ title: e.target.value })} data-testid={testid}
        className="w-full font-heading text-xl font-semibold bg-transparent outline-none focus:bg-muted/40 rounded-sm" placeholder="Titre de la section" />
    </div>
  );
}

function DiagramSectionEditor({ s, onChange, proposalId, companySlug }) {
  const [brief, setBrief] = useState('');
  const [generating, setGenerating] = useState(false);
  const subtype = s.subtype || 'process';

  const SUBTYPES = [
    { value: 'process', label: 'Processus' },
    { value: 'timeline', label: 'Planning' },
    { value: 'comparison', label: 'Comparatif' },
  ];

  const generate = async () => {
    if (!brief.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/diagram-generate`, { subtype, brief });
      onChange({ subtype: data.diagram.subtype, items: data.diagram.items });
      toast.success('Schéma généré');
    } catch {
      toast.error('Échec de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const isArrayItems = subtype !== 'comparison';
  const items = isArrayItems ? (Array.isArray(s.items) ? s.items : []) : [];
  const compItems = !isArrayItems ? (s.items || { headers: [], rows: [] }) : null;

  const addItem = () => {
    if (subtype === 'process') {
      onChange({ items: [...items, { id: crypto.randomUUID(), step: items.length + 1, title: '', description: '' }] });
    } else if (subtype === 'timeline') {
      onChange({ items: [...items, { id: crypto.randomUUID(), date_label: '', title: '', description: '', milestone: false }] });
    }
  };

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    onChange({ items: next });
  };

  const removeItem = (idx) => onChange({ items: items.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <input value={s.title} onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Titre du schéma"
        className="w-full font-heading text-xl font-bold bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1" />

      {/* Sélecteur sous-type */}
      <div className="flex gap-1">
        {SUBTYPES.map((st) => (
          <button key={st.value} onClick={() => onChange({ subtype: st.value, items: st.value === 'comparison' ? { headers: ['Nous', 'Concurrent'], rows: [] } : [] })}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${subtype === st.value ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/40'}`}>
            {st.label}
          </button>
        ))}
      </div>

      {/* Génération IA */}
      <div className="flex gap-2">
        <input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Décrivez votre schéma…"
          onKeyDown={(e) => e.key === 'Enter' && generate()}
          className="flex-1 text-sm border border-border rounded-sm px-3 py-1.5 outline-none focus:border-primary bg-transparent" />
        <Button size="sm" onClick={generate} disabled={generating || !brief.trim()} className="rounded-sm shrink-0">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        </Button>
      </div>

      {/* Éditeur d'items (process / timeline) */}
      {isArrayItems && (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={item.id || idx} className="flex gap-2 p-2 border border-border rounded-sm items-start">
              {subtype === 'process' && (
                <span className="w-5 h-5 rounded-full bg-muted text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{idx + 1}</span>
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <input value={item.title} onChange={(e) => updateItem(idx, { title: e.target.value })}
                  placeholder={subtype === 'timeline' ? 'Jalon' : "Titre de l'étape"}
                  className="w-full text-sm font-medium bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1" />
                <input value={item.description} onChange={(e) => updateItem(idx, { description: e.target.value })}
                  placeholder="Description courte"
                  className="w-full text-xs text-muted-foreground bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1" />
                {subtype === 'timeline' && (
                  <input value={item.date_label || ''} onChange={(e) => updateItem(idx, { date_label: e.target.value })}
                    placeholder="Date / période (ex : Semaine 1)"
                    className="w-full text-xs bg-transparent outline-none focus:bg-muted/40 rounded-sm px-1" />
                )}
              </div>
              <button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-destructive p-1 shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addItem} className="flex items-center gap-1 text-sm text-primary font-medium hover:underline">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      )}

      {/* Éditeur comparison (simplifié) */}
      {!isArrayItems && compItems && (
        <div className="text-xs text-muted-foreground p-2 bg-muted/40 rounded-sm">
          Tableau comparatif — utilisez la génération IA pour créer le contenu. Édition manuelle avancée disponible dans une prochaine version.
        </div>
      )}
    </div>
  );
}

function AIDialog({ open, setOpen, onResult, clientName, offerTypes, prospectContext }) {
  const [brief, setBrief] = useState("");
  const [transcript, setTranscript] = useState("");
  const [offer, setOffer] = useState(offerTypes?.[0] || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (offerTypes?.[0]) setOffer(offerTypes[0]); }, [offerTypes]);

  const generate = async () => {
    if (!brief.trim() && !transcript.trim()) return toast.error("Décrivez le besoin ou collez un transcript");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/generate", {
        brief, transcript, client_name: clientName, offer_type: offer,
        ...(prospectContext && { prospect_context: prospectContext }),
      });
      onResult(data);
      toast.success("Contenu généré par l'IA");
      setOpen(false);
      setBrief(""); setTranscript("");
    } catch (e) {
      toast.error("Erreur de génération IA");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Génération IA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {prospectContext && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-xs text-emerald-700">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>Contexte prospect inclus — l'IA adaptera la proposition à leur secteur.</span>
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Type d'offre</label>
            <Select value={offer} onValueChange={setOffer}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="ai-offer-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(offerTypes || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Décrivez le besoin du client</label>
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} data-testid="ai-brief-input"
              className="mt-1.5 rounded-sm" rows={3} placeholder="Ex : PME de 25 personnes, 2 sites, besoin d'une connexion fiable…" />
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-1.5">Transcript du call <span className="text-xs text-muted-foreground font-normal">(optionnel · le copywriting se cale sur ce que le client a dit)</span></label>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="ai-transcript-input"
              className="mt-1.5 rounded-sm" rows={4} placeholder="Collez ici le transcript de votre appel de découverte…" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={generate} disabled={loading} data-testid="ai-generate-submit" className="rounded-sm bg-primary hover:bg-primary/90">
            {loading ? "Génération…" : <><Sparkles className="w-4 h-4 mr-1" /> Générer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
